import React from 'react';
import { get, find, cloneDeep, merge, set, concat, debounce } from 'lodash';
import moment from 'moment';

// local dependencies
import { CmsWidgetService } from 'services/cmsWidget';
import { cmsWidget as CmsWidgetView } from 'ui/views/widgets/cmsWidget';
import { SERVICE_RESPONSE_STATUS, FORM_FIELDS, SIZES } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { getMemberManagementState } from 'resolvers/memberManagementState';

// get store
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CmsWidget } from 'ui/containers/CmsWidget';
 *
 * // render
 * <CmsWidget propName='propValue'/>
 */
export class CmsWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind functions
        this.__bind();

        // start new service instance
        this.cmsWidgetService = new CmsWidgetService();

        // initialise state
        this.state = {
            ...props,
            disableSubmit: true,
            closePopoverOnScrollTrigger: 0
        };
    }

    /**
     * @desc Handles binding of functions
     */
    __bind() {
        log.debug( 'CmsWidget.__bind()' );

        this.handleFormScroll = debounce( this.handleFormScroll, 500, { leading: true, trailing: false } ).bind( this );
        this.handleFormFieldChange = this.handleFormFieldChange.bind( this );
        this.handleCategoryTypeChange = this.handleCategoryTypeChange.bind( this );
        this.handleGrievanceModeChange = this.handleGrievanceModeChange.bind( this );
        this.handleInquiryModeChange = this.handleInquiryModeChange.bind( this );
        this.handleFormSubmit = this.handleFormSubmit.bind( this );
    }

    /**
     * @desc Executes on scroll event in form
     */
    handleFormScroll() {
        log.debug( 'CmsWidget.handleFormScroll()' );

        // toggle the state of trigger
        this.setState( state => {
            return { closePopoverOnScrollTrigger: state.closePopoverOnScrollTrigger ? 0 : 1 };
        } );
    }

    /**
     * @desc Handles form field change
     */
     async handleFormFieldChange( { values, field } ) {
        log.debug( 'CmsWidget.handleFormFieldChange()' );

        if ( FORM_FIELDS.CATEGORY_TYPE === field.name ) {
            this.handleCategoryTypeChange( { value: values[ field.name ] } );
        } else if ( FORM_FIELDS.GRIEVANCE_MODE === field.name ) {
            this.handleGrievanceModeChange( { value: values[ field.name ] } );
        } else if ( FORM_FIELDS.INQUIRY_MODE === field.name ) {
            this.handleInquiryModeChange( { value: values[ field.name ] } );
        }
    }

    /**
     * @desc Handles category type change
     */
    async handleCategoryTypeChange( { value } ) {
        log.debug( 'CmsWidget.handleCategoryTypeChange()' );

        // get url associated with selected value
        const urlMap = get( this.state, 'urlMap', [] );
        const urlObj = find( urlMap, { name: value }, {} );
        const url = get( urlObj, 'url', {} );

        // show page level preloader
        pageLevelPreloader.show();
        
        // cancel ongoing service
        this.cmsWidgetService.cancel();

        // get status and widget data corresponding to above selected value
        const { status, data } = await this.cmsWidgetService.fetchCategoryWidgetData( {
            url,
            data: {
                context: get( this.props, 'context' ),
                data: {
                    categoryType: value
                }
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // clone current state
        let _state = cloneDeep( this.state );

        // check if status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // merge props' data with fetched data (options and values)
            const propsData = cloneDeep( get( this.props, 'data' ) );
            const newData = merge( propsData, get( data, 'data' ) );

            // set category type to current selected value
            set( newData, 'state.values.categoryType', value );

            // update state with new data
            set( _state, 'actions', get( data, 'actions' ) );
            set( _state, 'data', newData );
            set( _state, 'fields', concat( get( this.props, 'fields' ), get( data, 'fields' ) ) );

            // update submit button state
            set( _state, 'disableSubmit', false );

            // set updated state
            this.setState( _state );
        } else {

            // set current state to initial state
            _state = {
                ...this.props,
                disableSubmit: true
            };

            // update state and show error overlay
            this.setState( _state, () => errorOverlay.open() );
        }
    }

    /**
     * @desc Handles grievance mode change
     */
    async handleGrievanceModeChange( { value } ) {
        log.debug( 'CmsWidget.handleGrievanceModeChange()' );

        // clone current state
        const _state = cloneDeep( this.state );

        // get option object corresponding to selected grievance mode
        const grievanceModeOptions = get( _state, 'data.options.grievanceModeOptions' );
        const grievanceModeSelectedOption = find( grievanceModeOptions, { value } );

        // get options for mode type corresponding to selected grievance mode
        const modeTypeOptions = get( grievanceModeSelectedOption, 'options' );

        // set mode type options in state
        set( _state, 'data.options.modeTypeOptions', modeTypeOptions );

        // update state
        this.setState( _state );
    }

    /**
     * @desc Handles inquiry mode change
     */
    async handleInquiryModeChange ( { value } ) {
        log.debug( 'CmsWidget.handleInquiryModeChange()' );

        // clone current state
        const _state = cloneDeep( this.state );

        // get option object corresponding to selected inquiry mode
        const inquiryModeOptions = get( _state, 'data.options.inquiryModeOptions' );
        const inquiryModeSelectedOption = find( inquiryModeOptions, { value } );

        // get options for mode type corresponding to selected inquiry mode
        const modeTypeOptions = get( inquiryModeSelectedOption, 'options' );

        // set mode type options in state
        set( _state, 'data.options.modeTypeOptions', modeTypeOptions );

        // update state
        this.setState( _state );
    }

    /**
     * @desc Handles form submission
     */
    async handleFormSubmit ( formData ) {
        log.debug( 'CmsWidget.handleFormSubmit()' );

        // get call id from call state
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        let requestTime = null;

        // check if call id is available
        if ( callId ) {

            // get call start time from redux CALL_STATE and format it
            const callStartTime = get( store.getState()[ REDUCER.CALL_STATE ], 'callStartTime' );
            requestTime = moment( callStartTime ).format();
        } else {

            // get member management landing timestamp from member management state
            requestTime = get( getMemberManagementState(), 'timestamp', null );
        }

        // get submit action object
        const submitAction = find( this.state.actions, { name: 'submit' } );

        // close overlay
        this.props.closeOverlay();

        // show page preloader
        pageLevelPreloader.show();
        
        // cancel ongoing service
        this.cmsWidgetService.cancel();

        // get status of form submission
        const { status, data } = await this.cmsWidgetService.submitFormData( {
            url: get( submitAction, 'url' ),
            data: {
                context: get( this.props, 'context' ),
                data: {
                    ...formData,
                    requestTime,
                    submitTime: moment().format()
                }
            }
        } );

        // hide page preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // get sub message from response data
            const subMessage = getLabel( get( data, 'message' ), get( data, 'data' ) );

            // show success overlay
            successOverlay.open( {
                message: getLabel( get( data, 'title' ) ),
                config: {
                    autoClose: false,
                    overlaySize: SIZES.LARGE,
                    messageSize: SIZES.LARGE,
                    subMessage
                }
            } );
        } else {
            errorOverlay.open(); // show error overlay
        }
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <CmsWidgetView
                { ...this.state }
                onChangeCallBack={ this.handleFormFieldChange }
                onFormSubmit={ this.handleFormSubmit }
                onScroll={ this.handleFormScroll }
            />
        );
    }
}

// set display name
CmsWidget.displayName = 'CmsWidget';

// set default props
CmsWidget.defaultProps = {};

// prop types
CmsWidget.propTypes = {};
